import { useEffect, useState } from "react";
import { api, getAdminToken, setAdminToken } from "../lib/api";
import { CATEGORIES } from "../lib/categories";
import { toast } from "sonner";
import { Trash2, Plus, KeyRound, Sprout, Pencil, X, Users, Mail, Phone, Save, Heart, MapPin, Download } from "lucide-react";

const CITIES = ["Hyderabad", "Bangalore", "Pune"];

const EMPTY_EVENT = {
  title: "", category: "coffee-tasting", description: "", host_name: "",
  venue: "", city: "Hyderabad", starts_at: "", duration_minutes: 90,
  capacity: 12, cover_image: "",
};

const TABS = [
  { id: "submissions", label: "Submissions" },
  { id: "copartners", label: "Co-partners" },
  { id: "events", label: "Gatherings" },
  { id: "wishlist", label: "Wishlist" },
  { id: "about", label: "About page" },
];

// --- shared helpers ---
const headers = () => ({ "X-Admin-Token": getAdminToken() });

function isoForInput(iso) {
  if (!iso) return "";
  // datetime-local needs YYYY-MM-DDTHH:mm
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// --- Events tab ---
function EventsTab() {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(EMPTY_EVENT);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showInterests, setShowInterests] = useState(null);
  const [interests, setInterests] = useState([]);

  const load = () => api.get("/events").then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, starts_at: new Date(form.starts_at).toISOString() };
      if (editingId) {
        await api.patch(`/events/${editingId}`, payload, { headers: headers() });
        toast.success("Gathering updated.");
      } else {
        await api.post("/events", payload, { headers: headers() });
        toast.success("Gathering created.");
      }
      setForm(EMPTY_EVENT); setEditingId(null); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  const startEdit = (e) => {
    setEditingId(e.id);
    setForm({ ...e, starts_at: isoForInput(e.starts_at) });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this gathering?")) return;
    await api.delete(`/events/${id}`, { headers: headers() });
    toast.success("Removed."); load();
  };

  const viewInterests = async (e) => {
    setShowInterests(e);
    const { data } = await api.get(`/events/${e.id}/interests`, { headers: headers() });
    setInterests(data);
  };

  return (
    <div className="grid lg:grid-cols-12 gap-10">
      <form onSubmit={submit} className="lg:col-span-5 card-warm p-7 space-y-4" data-testid="admin-event-form">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-3xl">{editingId ? "Edit gathering" : "New gathering"}</h2>
          {editingId && (
            <button type="button" onClick={() => { setEditingId(null); setForm(EMPTY_EVENT); }}
              className="btn-ghost text-xs" data-testid="cancel-edit-event"><X size={14} /> Cancel</button>
          )}
        </div>
        <input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="form-title" />
        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="form-category">
          {CATEGORIES.filter((c) => c.id !== "all").map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <select value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="form-city">
          {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <textarea placeholder="Description" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required data-testid="form-description" />
        <input placeholder="Host name" value={form.host_name} onChange={(e) => setForm({ ...form, host_name: e.target.value })} required data-testid="form-host" />
        <input placeholder="Venue" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} required data-testid="form-venue" />
        <input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} required data-testid="form-starts" />
        <div className="grid grid-cols-2 gap-3">
          <input type="number" placeholder="Duration (min)" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })} data-testid="form-duration" />
          <input type="number" placeholder="Capacity" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: Number(e.target.value) })} data-testid="form-capacity" />
        </div>
        <input placeholder="Cover image URL" value={form.cover_image || ""} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} data-testid="form-cover" />
        <button className="btn-primary w-full justify-center" disabled={saving} type="submit" data-testid="form-submit">
          {editingId ? <Save size={16} /> : <Plus size={16} />} {saving ? "Saving…" : (editingId ? "Save changes" : "Create gathering")}
        </button>
      </form>

      <div className="lg:col-span-7">
        <h2 className="font-serif text-3xl mb-4">All gatherings ({events.length})</h2>
        <div className="space-y-3">
          {events.map((e) => (
            <div key={e.id} data-testid={`admin-event-${e.id}`} className="card-warm p-5 flex items-center gap-4">
              {e.cover_image && <img src={e.cover_image} alt="" className="w-20 h-20 object-cover rounded-xl" />}
              <div className="flex-1 min-w-0">
                <div className="font-serif text-xl truncate">{e.title}</div>
                <div className="text-sm" style={{ color: "var(--text-muted)" }}>
                  {e.city} · {e.category} · {new Date(e.starts_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })} · {e.interested_count || 0} interested
                </div>
              </div>
              <button onClick={() => viewInterests(e)} className="btn-ghost" data-testid={`view-rsvps-${e.id}`} aria-label="View RSVPs"><Users size={18} /></button>
              <button onClick={() => startEdit(e)} className="btn-ghost" data-testid={`edit-${e.id}`} aria-label="Edit"><Pencil size={18} /></button>
              <button onClick={() => remove(e.id)} className="btn-ghost" data-testid={`delete-${e.id}`} aria-label="Delete"><Trash2 size={18} /></button>
            </div>
          ))}
        </div>
      </div>

      {showInterests && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
             style={{ background: "rgba(45,36,30,0.45)", backdropFilter: "blur(6px)" }}
             onClick={() => setShowInterests(null)}>
          <div className="card-warm w-full max-w-lg p-7 max-h-[80vh] overflow-y-auto"
               style={{ background: "var(--bg)" }}
               onClick={(ev) => ev.stopPropagation()}
               data-testid="rsvps-modal">
            <div className="flex items-start justify-between">
              <div>
                <div className="label-eyebrow">RSVPs</div>
                <h3 className="font-serif text-2xl mt-2">{showInterests.title}</h3>
              </div>
              <button className="btn-ghost" onClick={() => setShowInterests(null)} aria-label="Close" data-testid="rsvps-modal-close"><X size={18} /></button>
            </div>
            <div className="mt-5 space-y-3">
              {interests.length === 0 && <p className="text-sm" style={{ color: "var(--text-muted)" }}>No RSVPs yet.</p>}
              {interests.map((i, idx) => (
                <div key={idx} className="p-3 rounded-2xl" style={{ background: "var(--surface)" }}>
                  <div className="font-medium">{i.name || "Anonymous"}</div>
                  <div className="text-xs mt-1 flex flex-wrap gap-3" style={{ color: "var(--text-muted)" }}>
                    {i.email && <span className="inline-flex items-center gap-1"><Mail size={12} /> {i.email}</span>}
                    {i.phone && <span className="inline-flex items-center gap-1"><Phone size={12} /> {i.phone}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Submissions tab (the new main funnel) ---
const GIG_ROLES = [
  { id: "paid-gigs", label: "Paid gigs" },
  { id: "volunteer", label: "Volunteering" },
  { id: "either", label: "Either / both" },
];
const GIG_ROLE_IDS = GIG_ROLES.map((r) => r.id);

function SubmissionsTab() {
  const [subs, setSubs] = useState([]);
  const [city, setCity] = useState("all");
  const [category, setCategory] = useState("all");
  const [kind, setKind] = useState("all"); // all | rsvp | gig

  const load = () => {
    const params = {};
    if (city !== "all") params.city = city;
    if (category !== "all") params.category = category;
    return api.get("/interests", { params, headers: headers() }).then((r) => setSubs(r.data));
  };
  useEffect(() => { load(); }, [city, category]);

  const filteredSubs = subs.filter((s) => {
    if (kind === "all") return true;
    const isGig = GIG_ROLE_IDS.includes(s.category);
    return kind === "gig" ? isGig : !isGig;
  });

  const remove = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    await api.delete(`/interests/${id}`, { headers: headers() });
    toast.success("Removed."); load();
  };

  const toggleStatus = async (s) => {
    const next = s.status === "new" ? "contacted" : "new";
    await api.patch(`/interests/${s.id}?status=${next}`, {}, { headers: headers() });
    toast.success(`Marked ${next}.`); load();
  };

  const exportCsv = () => {
    if (filteredSubs.length === 0) {
      toast.error("Nothing to export yet.");
      return;
    }
    const cols = [
      ["Type", (s) => GIG_ROLE_IDS.includes(s.category) ? "Gig" : "RSVP"],
      ["Name", (s) => s.name || ""],
      ["Phone", (s) => s.phone || ""],
      ["Email", (s) => s.email || ""],
      ["Category", (s) => s.category || ""],
      ["City", (s) => s.city || ""],
      ["For whom", (s) => s.for_whom || ""],
      ["Status", (s) => s.status || ""],
      ["Comments", (s) => s.comments || ""],
      ["Created at", (s) => s.created_at || ""],
    ];
    const esc = (val) => {
      const str = String(val ?? "");
      if (/[",\n\r]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
      return str;
    };
    const header = cols.map(([h]) => esc(h)).join(",");
    const rows = filteredSubs.map((s) => cols.map(([, fn]) => esc(fn(s))).join(","));
    const csv = [header, ...rows].join("\r\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `saamne-submissions-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredSubs.length} row${filteredSubs.length === 1 ? "" : "s"}.`);
  };

  return (
    <div data-testid="admin-submissions">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
        <h2 className="font-serif text-3xl">Submissions ({filteredSubs.length})</h2>
        <button
          onClick={exportCsv}
          className="btn-secondary text-sm"
          data-testid="export-csv-btn"
          disabled={filteredSubs.length === 0}
        >
          <Download size={16} /> Export CSV
        </button>
      </div>
      <p className="text-sm mb-6" style={{ color: "var(--text-muted)" }}>
        RSVPs and gig applications — everyone who reached out via the home page or "Gig with us." Filter what you want, then export.
      </p>

      <div className="mb-3">
        <div className="label-eyebrow mb-2">Type</div>
        <div className="flex flex-wrap gap-2">
          {[
            { id: "all", label: "All submissions" },
            { id: "rsvp", label: "RSVPs (interest)" },
            { id: "gig", label: "Gig applications" },
          ].map((k) => (
            <button
              key={k.id}
              onClick={() => setKind(k.id)}
              data-testid={`sub-kind-${k.id}`}
              data-active={kind === k.id}
              className="chip"
            >{k.label}</button>
          ))}
        </div>
      </div>

      <div className="mb-3">
        <div className="label-eyebrow mb-2">City</div>
        <div className="flex flex-wrap gap-2">
          {["all", "Hyderabad", "Bangalore", "Pune"].map((c) => (
            <button key={c} onClick={() => setCity(c)} data-testid={`sub-city-${c.toLowerCase()}`} data-active={city === c} className="chip">{c === "all" ? "All cities" : c}</button>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <div className="label-eyebrow mb-2">Category</div>
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button key={c.id} onClick={() => setCategory(c.id)} data-testid={`sub-cat-${c.id}`} data-active={category === c.id} className="chip">{c.label}</button>
          ))}
          {GIG_ROLES.map((r) => (
            <button key={r.id} onClick={() => setCategory(r.id)} data-testid={`sub-cat-${r.id}`} data-active={category === r.id} className="chip">{r.label}</button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {filteredSubs.map((s) => {
          const isGig = GIG_ROLE_IDS.includes(s.category);
          return (
          <div key={s.id} className="card-warm p-5" data-testid={`submission-${s.id}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <div className="flex items-baseline gap-3 flex-wrap">
                <span
                  className="chip"
                  data-testid={`type-${s.id}`}
                  style={{
                    background: isGig ? "var(--primary)" : "var(--surface)",
                    color: isGig ? "var(--bg)" : "var(--text-main)",
                    borderColor: isGig ? "var(--primary)" : "var(--line)",
                  }}
                >{isGig ? "Gig" : "RSVP"}</span>
                <h3 className="font-serif text-xl">{s.name}</h3>
                <span className="text-sm inline-flex items-center gap-1" style={{ color: "var(--text-muted)" }}><Phone size={13} /> {s.phone}</span>
                {s.email && <span className="text-sm inline-flex items-center gap-1" style={{ color: "var(--text-muted)" }}><Mail size={13} /> {s.email}</span>}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => toggleStatus(s)} className="chip" data-active={s.status === "contacted"} data-testid={`status-${s.id}`}>
                  {s.status === "contacted" ? "✓ contacted" : "new"}
                </button>
                <button onClick={() => remove(s.id)} className="btn-ghost" data-testid={`delete-sub-${s.id}`}><Trash2 size={16} /></button>
              </div>
            </div>
            <div className="text-xs mt-2 flex items-center gap-3 flex-wrap" style={{ color: "var(--text-muted)" }}>
              <span>{s.category}</span>
              <span className="inline-flex items-center gap-1"><MapPin size={12} /> {s.city}</span>
              {s.for_whom && !isGig && <span>for {s.for_whom === "someone-i-look-after" ? "someone they look after" : "themselves"}</span>}
              <span>{new Date(s.created_at).toLocaleString("en-IN")}</span>
            </div>
            {s.comments && <p className="mt-3 text-sm" style={{ color: "var(--text-main)" }}>"{s.comments}"</p>}
          </div>
        );
        })}
        {filteredSubs.length === 0 && <p className="text-sm" style={{ color: "var(--text-muted)" }}>No submissions yet.</p>}
      </div>
    </div>
  );
}

// --- Co-partners tab ---
function CopartnersTab() {
  const [items, setItems] = useState([]);
  const [city, setCity] = useState("all");

  const load = () => {
    const params = {};
    if (city !== "all") params.city = city;
    return api.get("/copartners", { params, headers: headers() }).then((r) => setItems(r.data));
  };
  useEffect(() => { load(); }, [city]);

  const remove = async (id) => {
    if (!window.confirm("Remove this co-partner note?")) return;
    await api.delete(`/copartners/${id}`, { headers: headers() });
    toast.success("Removed."); load();
  };

  const toggleStatus = async (s) => {
    const next = s.status === "new" ? "contacted" : "new";
    await api.patch(`/copartners/${s.id}?status=${next}`, {}, { headers: headers() });
    toast.success(`Marked ${next}.`); load();
  };

  return (
    <div data-testid="admin-copartners">
      <h2 className="font-serif text-3xl mb-3">Co-partners ({items.length})</h2>
      <p className="text-sm mb-6" style={{ color: "var(--text-muted)" }}>
        People offering to co-host — a venue, a skill, food, gear, or a Sunday morning.
      </p>
      <div className="flex flex-wrap gap-2 mb-6">
        {["all", "Hyderabad", "Bangalore", "Pune"].map((c) => (
          <button key={c} onClick={() => setCity(c)} data-testid={`cop-city-filter-${c.toLowerCase()}`} data-active={city === c} className="chip">{c === "all" ? "All cities" : c}</button>
        ))}
      </div>
      <div className="space-y-3">
        {items.map((s) => (
          <div key={s.id} className="card-warm p-5" data-testid={`copartner-${s.id}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <div className="flex items-baseline gap-3 flex-wrap">
                <h3 className="font-serif text-xl">{s.name}</h3>
                <span className="text-sm inline-flex items-center gap-1" style={{ color: "var(--text-muted)" }}><Phone size={13} /> {s.phone}</span>
                {s.email && <span className="text-sm inline-flex items-center gap-1" style={{ color: "var(--text-muted)" }}><Mail size={13} /> {s.email}</span>}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => toggleStatus(s)} className="chip" data-active={s.status === "contacted"} data-testid={`cop-status-${s.id}`}>
                  {s.status === "contacted" ? "✓ contacted" : "new"}
                </button>
                <button onClick={() => remove(s.id)} className="btn-ghost" data-testid={`delete-cop-${s.id}`}><Trash2 size={16} /></button>
              </div>
            </div>
            <div className="text-xs mt-2 flex items-center gap-3 flex-wrap" style={{ color: "var(--text-muted)" }}>
              <span>{s.category}</span>
              <span className="inline-flex items-center gap-1"><MapPin size={12} /> {s.city}</span>
              {Array.isArray(s.bringing) && s.bringing.length > 0 && (
                <span>brings: {s.bringing.join(", ")}</span>
              )}
              <span>{new Date(s.created_at).toLocaleString("en-IN")}</span>
            </div>
            {s.message && <p className="mt-3 text-sm" style={{ color: "var(--text-main)" }}>"{s.message}"</p>}
          </div>
        ))}
        {items.length === 0 && <p className="text-sm" style={{ color: "var(--text-muted)" }}>No co-partner notes yet.</p>}
      </div>
    </div>
  );
}

// --- Wishlist tab ---
function WishlistTab() {
  const [wishes, setWishes] = useState([]);
  const load = () => api.get("/wishes").then((r) => setWishes(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Remove this wish?")) return;
    await api.delete(`/wishes/${id}`, { headers: headers() });
    toast.success("Removed."); load();
  };

  return (
    <div data-testid="admin-wishlist">
      <h2 className="font-serif text-3xl mb-6">Community wishlist ({wishes.length})</h2>
      <p className="text-sm mb-6" style={{ color: "var(--text-muted)" }}>
        What people are asking us to host across Hyderabad, Bangalore and Pune — sorted by most-loved.
      </p>
      <div className="grid md:grid-cols-2 gap-4">
        {wishes.map((w) => (
          <div key={w.id} className="card-warm p-5" data-testid={`admin-wish-${w.id}`}>
            <div className="flex items-baseline gap-3 text-xs">
              <span className="label-eyebrow">{w.category}</span>
              <span className="inline-flex items-center gap-1" style={{ color: "var(--text-muted)" }}><MapPin size={12} /> {w.city}</span>
              <span className="ml-auto inline-flex items-center gap-1 font-medium" style={{ color: "var(--primary)" }}><Heart size={13} /> {w.vote_count}</span>
            </div>
            <h3 className="font-serif text-xl mt-2 leading-snug">{w.title}</h3>
            <p className="text-sm mt-2" style={{ color: "var(--text-muted)" }}>{w.description}</p>
            <div className="text-xs mt-3 flex items-center gap-3 flex-wrap" style={{ color: "var(--text-muted)" }}>
              {w.suggested_by && <span>by {w.suggested_by}</span>}
              {w.phone && <span className="inline-flex items-center gap-1"><Phone size={12} /> {w.phone}</span>}
              {w.email && <span className="inline-flex items-center gap-1"><Mail size={12} /> {w.email}</span>}
              <button onClick={() => remove(w.id)} className="btn-ghost ml-auto" data-testid={`delete-wish-${w.id}`} aria-label="Delete"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        {wishes.length === 0 && <p className="text-sm" style={{ color: "var(--text-muted)" }}>No suggestions yet.</p>}
      </div>
    </div>
  );
}

// --- About tab ---
function AboutTab() {
  const [page, setPage] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/pages/about")
      .then((r) => setPage(r.data))
      .catch(() => setPage({ slug: "about", title: "Why saamne?", body: "" }));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/pages/about", { title: page.title, body: page.body }, { headers: headers() });
      toast.success("About page saved.");
    } catch {
      toast.error("Save failed.");
    } finally { setSaving(false); }
  };

  if (!page) return <p>Loading…</p>;
  return (
    <form onSubmit={save} className="max-w-3xl card-warm p-7 space-y-4" data-testid="admin-about-form">
      <h2 className="font-serif text-3xl">About page</h2>
      <input value={page.title} onChange={(e) => setPage({ ...page, title: e.target.value })} placeholder="Title" data-testid="about-title" />
      <textarea rows={14} value={page.body} onChange={(e) => setPage({ ...page, body: e.target.value })} placeholder="Body — paragraphs separated by blank lines" data-testid="about-body" />
      <button type="submit" disabled={saving} className="btn-primary" data-testid="about-save"><Save size={16} /> {saving ? "Saving…" : "Save About page"}</button>
    </form>
  );
}

// --- Top-level Admin ---
export default function Admin() {
  const [token, setToken] = useState(getAdminToken());
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [tab, setTab] = useState("submissions");

  const submitToken = (e) => {
    e.preventDefault();
    setAdminToken(token.trim());
    setAuthed(true);
    toast.success("Welcome in.");
  };

  const seed = async () => {
    try {
      const { data } = await api.post("/admin/seed", {}, { headers: headers() });
      toast.success(`Seeded: ${data.events_added} events, ${data.articles_added} essays.`);
    } catch { toast.error("Seed failed."); }
  };

  if (!authed) {
    return (
      <div data-testid="admin-login-page" className="max-w-md mx-auto px-6 pt-24 pb-20">
        <div className="label-eyebrow">Admin</div>
        <h1 className="font-serif text-4xl mt-3">A small door.</h1>
        <p className="mt-3 text-sm" style={{ color: "var(--text-muted)" }}>Enter the admin token.</p>
        <form onSubmit={submitToken} className="mt-8 space-y-4">
          <input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Admin token" data-testid="admin-token-input" />
          <button className="btn-primary w-full justify-center" type="submit" data-testid="admin-login-btn">
            <KeyRound size={16} /> Enter
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="max-w-6xl mx-auto px-6 md:px-10 pt-12 pb-20">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Admin</div>
          <h1 className="font-serif text-5xl mt-3">Tend the season.</h1>
        </div>
        <div className="flex gap-3">
          <button onClick={seed} className="btn-secondary text-sm" data-testid="admin-seed-btn"><Sprout size={16} /> Seed sample data</button>
          <button
            onClick={() => { localStorage.removeItem("saamne_admin_token"); setAuthed(false); setToken(""); }}
            className="btn-ghost text-sm" data-testid="admin-logout-btn">Sign out</button>
        </div>
      </div>

      <div className="flex gap-2 mb-10 border-b" style={{ borderColor: "var(--line)" }} data-testid="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className="px-5 py-3 text-sm transition-colors"
            style={{
              borderBottom: tab === t.id ? "2px solid var(--primary)" : "2px solid transparent",
              color: tab === t.id ? "var(--primary)" : "var(--text-main)",
              fontWeight: tab === t.id ? 500 : 400,
            }}
          >{t.label}</button>
        ))}
      </div>

      {tab === "submissions" && <SubmissionsTab />}
      {tab === "copartners" && <CopartnersTab />}
      {tab === "events" && <EventsTab />}
      {tab === "wishlist" && <WishlistTab />}
      {tab === "about" && <AboutTab />}
    </div>
  );
}
